import { useState } from 'react';

import { useEntityForm, type EntityForm } from '@/components/forms/useEntityForm';
import { useDialogService } from '@/components/ui';

/**
 * Speichern und Löschen der vier Administrationsformulare. Die Abläufe sind in
 * `IwmAdminBenutzerForm.razor`, `IwmAdminGruppeForm.razor`, `IwmAdminKategorieForm.razor`
 * und `IwmAdminViewForm.razor` identisch — nur Dienst und Rückfragetext unterscheiden sich.
 */
export interface AdminFormOptions<T extends { Id: number }> {
  id: number;
  createEmpty: () => T;
  fetch: (id: number) => Promise<T | null>;
  create: (model: T) => Promise<T | null>;
  put: (id: number, model: T) => Promise<T | null>;
  remove: (id: number) => Promise<unknown>;
  /** Text der Rückfrage von `Delete()`. */
  deleteMessage: string;
  /** Titel der Rückfrage von `Delete()`. */
  deleteTitle: string;
  onSave: (id: number) => void | Promise<void>;
}

export interface AdminForm<T> extends EntityForm<T> {
  /** Weicht das Modell vom zuletzt geladenen oder gespeicherten Stand ab? */
  dirty: boolean;
  /** `New` — leeres Modell wie `ResetForm(true)`. */
  onNew: () => void;
  onSave: () => Promise<void>;
  onDelete: () => Promise<void>;
}

/**
 * Modelle, die einen geladenen Stand darstellen — frisch aus `fetch`, aus `createEmpty`
 * oder als Antwort auf das Anlegen. `useEntityForm` legt sie unverändert als Modell ab und
 * ersetzt sie bei jeder Eingabe durch eine Kopie; steht eines davon im Formular, ist es
 * also unverändert. So erkennt der Hook ein Neuladen, ohne in `useEntityForm` einzugreifen.
 */
const loadedModels = new WeakSet<object>();

function markLoaded<T extends object>(model: T): T {
  loadedModels.add(model);
  return model;
}

export function useAdminForm<T extends { Id: number }>(options: AdminFormOptions<T>): AdminForm<T> {
  const { createEmpty, fetch, id } = options;
  const dialogService = useDialogService();
  const form = useEntityForm<T>({
    id,
    createEmpty: () => markLoaded(createEmpty()),
    fetch: async (entryId) => {
      const loaded = await fetch(entryId);
      return loaded === null ? null : markLoaded(loaded);
    },
  });

  // Stand, gegen den `dirty` vergleicht. Er folgt in der Renderphase jedem frisch
  // geladenen Modell; ein Effekt hätte einen Durchlauf lang den alten Stand gezeigt.
  const [loaded, setLoaded] = useState<T>(form.model);
  if (loaded !== form.model && loadedModels.has(form.model)) setLoaded(form.model);
  const baseline = loadedModels.has(form.model) ? form.model : loaded;
  const dirty = JSON.stringify(form.model) !== JSON.stringify(baseline);

  const onSave = async () => {
    if (!form.isValid) return;
    form.setSaving(true);
    const currentId = form.model.Id;
    let saved = form.model;
    if (currentId > 0) {
      // Nur ein erfolgreiches Speichern wird zum neuen Stand; `APIBase` meldet Fehler durch `null`.
      if ((await options.put(currentId, form.model)) !== null) setLoaded(saved);
    } else {
      // Der Model-Setter von `IwmBaseForm` ersetzt ein leeres Ergebnis durch ein neues Modell.
      saved = markLoaded((await options.create(form.model)) ?? createEmpty());
      form.setModel(saved);
    }
    form.setSaving(false);
    await options.onSave(currentId === 0 ? saved.Id : currentId);
  };

  const onDelete = async () => {
    const confirmed = await dialogService.confirm(options.deleteMessage, options.deleteTitle);
    if (!confirmed) return;
    await options.remove(form.model.Id);
    void options.onSave(0);
  };

  return {
    ...form,
    dirty,
    onNew: () => {
      form.reset();
    },
    onSave,
    onDelete,
  };
}
